import type { ApiClient, Query } from '@saas/ui';
import type { ContactSummary } from '@saas/module-crm/api';
import type {
  ContactDisclosure,
  CustomerRelationshipDetail,
  HandoverVisitResult,
  InteractionDetail,
  ManualInteractionKind,
  PlanHandoverVisitInput,
  UnmatchedInteraction,
  UserRelationshipDetail,
} from '../api/relationship.dto.js';

/**
 * relationship の HTTP 契約（ブラウザ側）。型は api/ の DTO をそのまま使う。
 * ★ HTTP を通ると DTO の `Date` は ISO 文字列で届く。表示は formatDate / formatDateTime（どちらも受ける）を通すこと。
 * クエリキーはここで一元管理する（シェルが未確定の件数を出すときも同じキー）。
 */

export const relationshipKeys = {
  customer: (customerId: string) => ['relationship', 'customers', customerId] as const,
  interactions: (customerId: string, kind?: string) => ['relationship', 'customers', customerId, 'interactions', kind ?? 'all'] as const,
  user: (userId: string) => ['relationship', 'users', userId] as const,
  unmatched: (scope: 'mine' | 'all') => ['relationship', 'unmatched', scope] as const,
  unmatchedCount: () => ['relationship', 'unmatched', 'count'] as const,
  /** 名寄せの候補に出す先方の連絡先（relationship のサーバ側の口 → crm の `listContactsForCustomer`） */
  customerContacts: (customerId: string) => ['relationship', 'customers', customerId, 'contacts'] as const,
  /** 「1 問」の候補（出席した本人はいつでも読める） */
  interactionContacts: (interactionId: string) => ['relationship', 'interactions', interactionId, 'contacts'] as const,
  /** 開示請求（§17.9・contact 単位） */
  contactExport: (contactId: string) => ['relationship', 'contacts', contactId, 'export'] as const,
};

export interface RecordInteractionBody {
  customerId: string;
  kind: ManualInteractionKind;
  /** ISO 8601（オフセット付き） */
  occurredAt: string;
  durationMin?: number | null;
  location?: string | null;
  subject?: string | null;
  initiatedBy?: 'us' | 'them' | null;
  contactIds: string[];
  userIds?: string[];
}

export const relationshipApi = {
  /** 顧客の関係プロファイル（§17.6）。crm の顧客詳細から遷移する独立ページ */
  customer: (api: ApiClient, customerId: string) => api.get<CustomerRelationshipDetail>(`/relationship/customers/${customerId}`),
  interactions: (api: ApiClient, customerId: string, q: { limit?: number; before?: string; kind?: string } = {}) =>
    api.get<InteractionDetail[]>(`/relationship/customers/${customerId}/interactions`, q as Query),
  /** 従業員側（§17.7）。自分は userId = 'me' */
  user: (api: ApiClient, userId: string) => api.get<UserRelationshipDetail>(`/relationship/users/${userId}`),
  /**
   * 引き継ぎ先を決めて「同席の引き継ぎ訪問」を立てる（§17.7）。
   * calendar に予定を作る口がまだ無い環境では `created: false` で返る（画面は内容を見せて手で入れてもらう）
   */
  planHandover: (api: ApiClient, userId: string, body: PlanHandoverVisitInput) => api.post<HandoverVisitResult>(`/relationship/users/${userId}/handover`, body),
  unmatched: (api: ApiClient, q: { scope?: 'mine' | 'all'; limit?: number } = {}) => api.get<UnmatchedInteraction[]>('/relationship/unmatched', q as Query),
  unmatchedCount: (api: ApiClient) => api.get<{ count: number }>('/relationship/unmatched/count'),
  interaction: (api: ApiClient, id: string) => api.get<InteractionDetail>(`/relationship/interactions/${id}`),
  record: (api: ApiClient, body: RecordInteractionBody) => api.post<InteractionDetail>('/relationship/interactions', body),
  /** 「会議終了後の 1 問」をまとめて確定（空配列 = 分からない / 居ない） */
  confirmInteraction: (api: ApiClient, id: string, contactIds: string[]) => api.post<InteractionDetail>(`/relationship/interactions/${id}/confirm`, { contactIds }),
  /** 候補 1 件を 1 タップで確定。contactId = null は「先方の人ではない」 */
  confirmParticipant: (api: ApiClient, id: string, participantId: string, contactId: string | null) =>
    api.post<InteractionDetail>(`/relationship/interactions/${id}/participants/${participantId}/confirm`, { contactId }),
  addParticipant: (api: ApiClient, id: string, contactId: string) => api.post<InteractionDetail>(`/relationship/interactions/${id}/participants`, { contactId }),
  /** 先方の連絡先の一覧（確定の候補を出すためだけに読む）。顧客を読める人向け */
  customerContacts: (api: ApiClient, customerId: string) => api.get<{ items: ContactSummary[] }>(`/relationship/customers/${customerId}/contacts`, { limit: 200 } as Query),
  /** 同上。**出席した本人**は顧客の閲覧権限に関わらず読める（§17.9 の例外） */
  interactionContacts: (api: ApiClient, interactionId: string) =>
    api.get<{ customerId: string | null; items: ContactSummary[] }>(`/relationship/interactions/${interactionId}/contacts`, { limit: 200 } as Query),
  /** 開示請求（§17.9）。CSV は `?format=csv`（api.download） */
  contactExport: (api: ApiClient, contactId: string) => api.get<ContactDisclosure>(`/relationship/contacts/${contactId}/export`),
  contactExportCsv: (api: ApiClient, contactId: string) => api.download(`/relationship/contacts/${contactId}/export.csv`),
};
